<script>
import DataLayer from '@services/data-layer/index'
import SharedCarousel from '@components/carousel/shared-carousel'
import SharedCarouselSlide from '@components/carousel/shared-carousel-slide'
import MixLargeTile from '@components/mix-large-tile'
import MixGridTile from '@components/mix-grid-tile'
import PremiumBanner from '@components/premium-banner'

export default {
    components: { MixLargeTile, MixGridTile, SharedCarousel, SharedCarouselSlide, PremiumBanner },
    props: {
        nowPlaying: {
            type: Object,
            default: () => {},
        },
        channel: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            trendingMixes: [],
        }
    },
    computed: {
        trendingTitle() {
            return `Trending on ${this.channel?.title || ''}`.trim()
        },
        displayedMixes() {
            return this.trendingMixes.slice(0, 6)
        },
        bannerSource() {
            return `radio-channel-${this.$route.params.channelId}`
        },
    },
    watch: {
        '$route.params.channelId'() {
            this.loadTrendingMixes()
        },
    },
    mounted() {
        this.loadTrendingMixes()
    },
    methods: {
        async loadTrendingMixes() {
            const response = await DataLayer.mixes.getPopularByChannel(this.$route.params.channelId)
            this.trendingMixes = response.body
        },
    },
}
</script>

<template>
    <BaseContent>
        <BaseSection
            padded
            title="Now Playing"
        >
            <MixLargeTile
                v-if="nowPlaying"
                :mix="nowPlaying.mixes"
                :channel="channel"
            >
                <BaseButtonPlay
                    slot="button"
                    :resource="channel"
                    type="secondary"
                />
            </MixLargeTile>
        </BaseSection>
        <div class="premiumBanner">
            <PremiumBanner :source="bannerSource" />
        </div>
        <BaseSection
            v-if="displayedMixes.length"
            :title="trendingTitle"
            padded
        >
            <SharedCarousel
                :per-page-custom="[[0, 2], [768, 3], [1025, 6]]"
                :pagination-enabled="false"
                :scroll-per-page="false"
                :min-swipe-distance="100"
                :space-padding="0"
                :mouse-drag="displayedMixes.length > 1"
                class="carousel"
            >
                <SharedCarouselSlide
                    v-for="(mix, index) in displayedMixes"
                    :key="`channel-trending-${mix.id}-${index}`"
                    class="slide"
                >
                    <MixGridTile
                        :mix="mix"
                        :index="index"
                    />
                </SharedCarouselSlide>
            </SharedCarousel>
        </BaseSection>
    </BaseContent>
</template>

<style lang="scss" scoped>
@use '@design' as *;

// Align the banner with the padded sections above/below it (container width +
// side gutters), and reproduce the section vertical rhythm — a bare element
// between two sections breaks the `.section + .section` margin chain, so the
// top/bottom gaps are set explicitly here. Figma 822-442.
.premiumBanner {
    @include container;
    @include sidePadding;

    margin-top: $size-grid-padding * 3;
    margin-bottom: $size-grid-padding * 3;

    @include desktop {
        margin-top: $s-h;
        margin-bottom: $s-h;
    }
}

.carousel {
    position: relative;
    margin: 0 (-$size-grid-padding);
    overflow: visible;
}

.slide {
    position: relative;
    padding: 0 $size-grid-padding;

    &:hover,
    &:focus-within {
        z-index: 2;
    }
}

:deep(.carousel .sharedCarouselViewport),
:deep(.carousel .swiper-wrapper),
:deep(.carousel .sharedCarouselSlide) {
    overflow: visible;
}
</style>
